import { scopedThreadKey } from "@cmmd-forge/client-runtime";
import type { ScopedThreadRef } from "@cmmd-forge/contracts";
import {
  hydrateFilesFromPersisted,
  hydrateImagesFromPersisted,
} from "../../composerDraftAttachments";
import { useComposerDraftStore } from "../../composerDraftStore";
import {
  composerFileAttachmentStore,
  type BrowserFileAttachmentStore,
} from "../../composerFileAttachmentStore";
import type { DraftId } from "../../draftId";
import type { createPromptStashStore } from "../../promptStashStore";
import type { PromptStashEntry } from "../../promptStashStore";
import { readFileAsDataUrl } from "../ChatView.logic";
import { releaseUnreferencedComposerFile } from "../../composerFileAttachmentLifecycle";
import { buildPersistedComposerImageAttachments } from "./ChatComposer.logic";

type Target = ScopedThreadRef | DraftId;
type Stash = ReturnType<typeof createPromptStashStore>;
type Draft = NonNullable<
  ReturnType<ReturnType<typeof useComposerDraftStore.getState>["getComposerDraft"]>
>;
type ComposerDraftState = ReturnType<typeof useComposerDraftStore.getState>;
type ReadImage = typeof readFileAsDataUrl;
type EarlySaveResult = { readonly status: "empty" | "staged" };
type SerializedStashContent = {
  readonly status: "serialized";
  readonly attachments: Draft["persistedAttachments"];
  readonly files: ReturnType<typeof persistedFiles>;
};

export function createComposerStashActions(
  stash: Stash,
  readImage = readFileAsDataUrl,
  fileStore: Pick<BrowserFileAttachmentStore, "read"> = composerFileAttachmentStore,
) {
  const saving = new Set<string>();
  return {
    async save(target: Target) {
      const key = typeof target === "string" ? target : scopedThreadKey(target);
      const drafts = useComposerDraftStore.getState();
      const candidate = stashSaveCandidate(drafts.getComposerDraft(target));
      if (isEarlySaveResult(candidate)) return candidate;
      if (saving.has(key)) return { status: "busy" } as const;
      saving.add(key);
      try {
        return await serializeAndSaveDraft({
          target,
          draft: candidate,
          drafts,
          stash,
          readImage,
          fileStore,
        });
      } finally {
        saving.delete(key);
      }
    },
    restore(target: Target, entry: PromptStashEntry) {
      if (!stash.getState().contains(entry)) return { status: "unavailable" } as const;
      if ((entry.files?.length ?? 0) > 0) {
        return restoreFilesFromStash({ target, entry, stash, fileStore });
      }
      const images = hydrateImagesFromPersisted(entry.attachments);
      if (images.length !== entry.attachments.length) return { status: "destination" } as const;
      const drafts = useComposerDraftStore.getState();
      let sourceRemovalFailed = false;
      const restored = drafts.restoreComposerStash(target, {
        prompt: entry.prompt,
        images,
        files: [],
        attachments: [...entry.attachments],
        terminalContexts: entry.terminalContexts.map((context) => ({
          ...context,
        })),
        onPersisted: () => {
          const removed = stash.getState().remove(entry.id).ok;
          sourceRemovalFailed = !removed;
          return removed;
        },
      });
      if (!restored)
        return {
          status: sourceRemovalFailed ? "retained" : "destination",
        } as const;
      return { status: "restored" } as const;
    },
  };
}

function stashSaveCandidate(draft: Draft | null): Draft | EarlySaveResult {
  if (
    !draft ||
    (!draft.prompt &&
      !draft.images.length &&
      !(draft.files?.length ?? 0) &&
      !draft.terminalContexts.length)
  ) {
    return { status: "empty" };
  }
  return draft.stagedSubmission ? { status: "staged" } : draft;
}

function isEarlySaveResult(value: Draft | EarlySaveResult): value is EarlySaveResult {
  return "status" in value && (value.status === "empty" || value.status === "staged");
}

async function serializeAndSaveDraft(input: {
  readonly target: Target;
  readonly draft: Draft;
  readonly drafts: ComposerDraftState;
  readonly stash: Stash;
  readonly readImage: ReadImage;
  readonly fileStore: Pick<BrowserFileAttachmentStore, "read">;
}) {
  const content = await serializeStashContent(input);
  if (content.status !== "serialized") return content;
  const guard = canSaveStashContent(input.drafts, input.target, input.draft);
  if (guard) return guard;
  const result = input.stash.getState().save(createStashEntry(input.draft, content));
  if (!result.ok) return { status: "storage" } as const;
  input.drafts.clearComposerContent(input.target);
  releaseEvictedFiles(result.evicted);
  return { status: "saved", evicted: result.evicted } as const;
}

async function serializeStashContent(input: {
  readonly draft: Draft;
  readonly readImage: ReadImage;
  readonly fileStore: Pick<BrowserFileAttachmentStore, "read">;
}): Promise<SerializedStashContent | { readonly status: "images" | "files" }> {
  const attachments = await buildPersistedComposerImageAttachments({
    images: input.draft.images,
    existingPersistedAttachments: input.draft.persistedAttachments,
    readFileAsDataUrl: input.readImage,
  });
  if (attachments.length !== input.draft.images.length) return { status: "images" } as const;
  const files = persistedFiles(input.draft);
  const filesAreAvailable = await Promise.all(files.map((file) => input.fileStore.read(file)))
    .then(() => true)
    .catch(() => false);
  return filesAreAvailable ? { status: "serialized", attachments, files } : { status: "files" };
}

function persistedFiles(draft: Draft) {
  return (draft.files ?? []).map(({ file: _file, type: _type, ...attachment }) => attachment);
}

function canSaveStashContent(drafts: ComposerDraftState, target: Target, draft: Draft) {
  const current = drafts.getComposerDraft(target);
  if (current?.stagedSubmission) return { status: "staged" } as const;
  return sameContent(draft, current) ? null : ({ status: "changed" } as const);
}

function createStashEntry(draft: Draft, content: SerializedStashContent) {
  return {
    id: crypto.randomUUID(),
    createdAt: new Date().toISOString(),
    prompt: draft.prompt,
    attachments: content.attachments,
    files: content.files,
    terminalContexts: draft.terminalContexts,
  };
}

function releaseEvictedFiles(entry: PromptStashEntry | null | undefined): void {
  if (!entry) return;
  void Promise.all((entry.files ?? []).map((file) => releaseUnreferencedComposerFile(file.id)));
}

function sameContent(before: Draft, after: Draft | null): boolean {
  return (
    after !== null &&
    before.prompt === after.prompt &&
    before.images === after.images &&
    before.files === after.files &&
    before.terminalContexts === after.terminalContexts
  );
}

async function restoreFilesFromStash(input: {
  readonly target: Target;
  readonly entry: PromptStashEntry;
  readonly stash: Stash;
  readonly fileStore: Pick<BrowserFileAttachmentStore, "read">;
}) {
  const files = await hydrateFilesFromPersisted(input.entry.files ?? [], input.fileStore).catch(
    () => null,
  );
  if (!files || !input.stash.getState().contains(input.entry))
    return { status: "retained" } as const;
  const images = hydrateImagesFromPersisted(input.entry.attachments);
  if (images.length !== input.entry.attachments.length) return { status: "destination" } as const;
  const drafts = useComposerDraftStore.getState();
  let sourceRemovalFailed = false;
  const restored = drafts.restoreComposerStash(input.target, {
    prompt: input.entry.prompt,
    images,
    files,
    attachments: [...input.entry.attachments],
    terminalContexts: input.entry.terminalContexts.map((context) => ({ ...context })),
    onPersisted: () => {
      const removed = input.stash.getState().remove(input.entry.id).ok;
      sourceRemovalFailed = !removed;
      return removed;
    },
  });
  if (!restored) return { status: sourceRemovalFailed ? "retained" : "destination" } as const;
  return { status: "restored" } as const;
}
